import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Loader2, Video, Image as ImageIcon, Clock, CheckCircle2, Send, Plus } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Empty, TabHeading, inputCls, fmtDate } from "./shared";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function MediaTab({ athleteId, refreshKey }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [forms, setForms] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/videos?athlete_id=${athleteId}`);
      setItems(data);
    } catch { toast.error("Failed to load media."); } finally { setLoading(false); }
  }, [athleteId]);

  useEffect(() => { load(); }, [load, refreshKey]);

  const setForm = (id, patch) => setForms((p) => ({ ...p, [id]: { mechanics_score: "", coach_notes: "", feedback: [], ...p[id], ...patch } }));
  const addFbRow = (id) => setForm(id, { feedback: [...(forms[id]?.feedback || []), { timestamp: "", note: "" }] });
  const updFb = (id, i, k, v) => { const fb = [...(forms[id]?.feedback || [])]; fb[i] = { ...fb[i], [k]: v }; setForm(id, { feedback: fb }); };

  const submit = async (id) => {
    const f = forms[id] || {};
    try {
      await api.patch(`/videos/${id}/review`, {
        feedback: (f.feedback || []).filter((x) => x.timestamp || x.note),
        mechanics_score: f.mechanics_score ? parseFloat(f.mechanics_score) : null,
        coach_notes: f.coach_notes || null,
      });
      toast.success("Feedback sent to athlete."); load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const filtered = items.filter((v) => filter === "all" || v.media_type === filter);

  if (loading) return <div className="flex justify-center py-16 text-white/40"><Loader2 className="animate-spin" /></div>;

  return (
    <div data-testid="af-media">
      <TabHeading>Media Library</TabHeading>
      <div className="flex gap-2 mb-6">
        {["all", "video", "photo"].map((s) => (
          <button key={s} data-testid={`af-media-filter-${s}`} onClick={() => setFilter(s)} className={`px-4 py-2 text-xs uppercase tracking-widest font-bold border transition-colors ${filter === s ? "bg-white border-white text-black" : "border-white/20 text-white/60 hover:text-white"}`}>{s}</button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <Empty testid="af-media-empty">No media yet. Upload a video or photo from Quick Actions.</Empty>
      ) : (
        <div className="space-y-6" data-testid="af-media-list">
          {filtered.map((v) => {
            const f = forms[v.id] || {};
            const isPhoto = v.media_type === "photo";
            return (
              <div key={v.id} data-testid={`af-media-${v.id}`} className="bg-[#121212] border border-white/10 p-5 grid lg:grid-cols-2 gap-6">
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <span className="flex items-center gap-2 font-bold uppercase tracking-tight text-sm">
                      {isPhoto ? <ImageIcon size={15} className="text-white/60" /> : <Video size={15} className="text-white/60" />}
                      {v.kind}{v.category ? ` · ${v.category}` : ""}
                    </span>
                    <span className="text-white/40 text-xs">{fmtDate(v.created_at)}</span>
                  </div>
                  {isPhoto ? (
                    <img src={`${BACKEND}/api/videos/${v.id}/file`} alt={v.kind} className="w-full max-h-80 object-contain bg-black border border-white/10" data-testid={`af-photo-${v.id}`} />
                  ) : (
                    <video controls src={`${BACKEND}/api/videos/${v.id}/file`} className="w-full max-h-80 bg-black border border-white/10" data-testid={`af-video-${v.id}`} />
                  )}
                </div>
                <div>
                  <span className={`inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-bold px-2.5 py-1 border mb-3 ${v.status === "reviewed" ? "border-white bg-white/10" : "border-white/25 text-white/60"}`}>{v.status === "reviewed" ? <CheckCircle2 size={11} /> : <Clock size={11} />} {v.status.replace("_", " ")}</span>
                  {v.status === "reviewed" ? (
                    <div data-testid={`af-media-done-${v.id}`}>
                      {v.mechanics_score != null && <p className="font-mono-accent text-lg font-bold mb-2">Mechanics: {v.mechanics_score}/100</p>}
                      {v.coach_notes && <p className="text-white/70 text-sm mb-3">{v.coach_notes}</p>}
                      {(v.feedback || []).map((fb, i) => <div key={`${fb.timestamp}-${i}`} className="flex gap-3 text-sm mb-1"><span className="font-mono-accent text-white/50">{fb.timestamp}</span><span className="text-white/70">{fb.note}</span></div>)}
                    </div>
                  ) : (
                    <div className="space-y-3">
                      <div><label className="font-mono-accent text-[10px] tracking-[0.2em] uppercase text-white/40 block mb-1.5">Mechanics Score (0-100)</label>
                        <input data-testid={`af-media-score-${v.id}`} type="number" min="0" max="100" value={f.mechanics_score || ""} onChange={(e) => setForm(v.id, { mechanics_score: e.target.value })} className={inputCls} /></div>
                      <div><label className="font-mono-accent text-[10px] tracking-[0.2em] uppercase text-white/40 block mb-1.5">Coach Notes</label>
                        <textarea data-testid={`af-media-notes-${v.id}`} rows={2} value={f.coach_notes || ""} onChange={(e) => setForm(v.id, { coach_notes: e.target.value })} className={inputCls} /></div>
                      {!isPhoto && (
                        <div>
                          <div className="flex items-center justify-between mb-1.5"><label className="font-mono-accent text-[10px] tracking-[0.2em] uppercase text-white/40">Timestamped Feedback</label>
                            <button data-testid={`af-media-addfb-${v.id}`} onClick={() => addFbRow(v.id)} className="text-white/50 hover:text-white"><Plus size={14} /></button></div>
                          {(f.feedback || []).map((fb, i) => (
                            <div key={i} className="flex gap-2 mb-2">
                              <input placeholder="0:03" value={fb.timestamp} onChange={(e) => updFb(v.id, i, "timestamp", e.target.value)} className="w-20 bg-[#0A0A0A] border border-white/15 px-2 py-2 text-sm outline-none focus:border-white" />
                              <input placeholder="Note" value={fb.note} onChange={(e) => updFb(v.id, i, "note", e.target.value)} className={inputCls} />
                            </div>
                          ))}
                        </div>
                      )}
                      <button data-testid={`af-media-submit-${v.id}`} onClick={() => submit(v.id)} className="inline-flex items-center gap-2 bg-white text-black font-bold uppercase tracking-widest text-xs px-6 py-3 hover:bg-white/85 transition-colors"><Send size={14} /> Send Feedback</button>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
